import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { promptsAPI, agentsAPI, mcpServersAPI, categoriesAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import BookmarkButton from '../components/BookmarkButton';
import SEOHead from '../components/SEOHead';
import OnboardingModal from '../components/OnboardingModal';
import { ContentTypeSwitcher, TypeBadge, LoadingSpinner, EmptyState } from '../components/ui';
import { getContentTypeName, getContentTypeIcon } from '../utils/designTokens';

const Dashboard = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  // Get initial content type from URL or default to 'prompt'
  const initialType = searchParams.get('type') || 'prompt';

  const [contentType, setContentType] = useState(initialType);
  const [content, setContent] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [filters, setFilters] = useState({
    category: '',
    sortBy: 'created_at',
    search: '',
  });

  // Update URL when content type changes
  useEffect(() => {
    setSearchParams({ type: contentType });
  }, [contentType, setSearchParams]);

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    fetchContent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, contentType]);

  // Check if user should see onboarding
  useEffect(() => {
    if (currentUser) {
      const hasSeenOnboarding = localStorage.getItem('onboarding_completed');
      if (!hasSeenOnboarding) {
        const timer = setTimeout(() => setShowOnboarding(true), 800);
        return () => clearTimeout(timer);
      }
    }
  }, [currentUser]);

  const fetchCategories = async () => {
    try {
      const response = await categoriesAPI.getAll();
      setCategories(response.data.categories);
    } catch (error) {
      console.error('Fetch categories error:', error);
    }
  };

  const fetchContent = async () => {
    setLoading(true);
    try {
      let response;
      const params = {
        ...filters,
        category_id: filters.category,
        sort_by: filters.sortBy,
      };

      switch (contentType) {
        case 'agent':
          response = await agentsAPI.getAll(params);
          setContent(response.data.agents || []);
          break;
        case 'mcp_server':
          response = await mcpServersAPI.getAll(params);
          setContent(response.data.servers || []);
          break;
        case 'prompt':
        default:
          response = await promptsAPI.getAll(params);
          setContent(response.data.prompts || []);
          break;
      }
    } catch (error) {
      console.error(`Fetch ${contentType} error:`, error);
      toast.error(`Failed to load ${getContentTypeName(contentType, true).toLowerCase()}`);
    } finally {
      setLoading(false);
    }
  };

  const getCreateLink = () => {
    switch (contentType) {
      case 'agent':
        return '/agents/create';
      case 'mcp_server':
        return '/mcp-servers/create';
      case 'prompt':
      default:
        return '/prompts/create';
    }
  };

  const getDetailLink = (item) => {
    switch (contentType) {
      case 'agent':
        return `/agents/${item.id}`;
      case 'mcp_server':
        return `/mcp-servers/${item.id}`;
      case 'prompt':
      default:
        return `/prompts/${item.id}`;
    }
  };

  const renderContentCard = (item) => {
    return (
      <div
        key={item.id}
        className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md hover:shadow-xl transition card-hover relative"
      >
        <div
          onClick={() => navigate(getDetailLink(item))}
          className="cursor-pointer"
        >
          {/* Type Badge */}
          <div className="mb-3">
            <TypeBadge type={contentType} size="sm" />
          </div>

          <h3 className="text-xl font-semibold mb-2 line-clamp-2 text-gray-900 dark:text-white">
            {item.title}
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4 line-clamp-3">
            {item.description}
          </p>

          <div className="flex items-center justify-between text-sm mb-3">
            <span className="text-gray-500 dark:text-gray-400">
              by @{item.username || item.user?.username || 'Unknown'}
            </span>
            <span className="text-boop-primary font-semibold">
              ⭐ {(parseFloat(item.bayesian_rating || item.average_rating) || 0).toFixed(1)}
            </span>
          </div>

          {/* Tags or Tools/Capabilities */}
          <div className="flex gap-2 flex-wrap">
            {contentType === 'agent' && item.tools?.slice(0, 3).map((tool, i) => (
              <span
                key={i}
                className="px-2 py-1 bg-pink-100 dark:bg-pink-900/30 text-pink-700 dark:text-pink-300 text-xs rounded-full"
              >
                {typeof tool === 'string' ? tool : tool.name}
              </span>
            ))}
            {contentType === 'mcp_server' && item.compatible_clients?.slice(0, 3).map((client, i) => (
              <span
                key={i}
                className="px-2 py-1 bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300 text-xs rounded-full"
              >
                {client}
              </span>
            ))}
            {contentType === 'prompt' && item.tags?.slice(0, 3).map((tag, i) => (
              <span
                key={i}
                className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-xs rounded-full"
              >
                {tag}
              </span>
            ))}
          </div>
        </div>

        {/* Bookmark Button (Prompts only for now) */}
        {currentUser && contentType === 'prompt' && (
          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700 flex justify-end">
            <BookmarkButton
              promptId={item.id}
              className="w-auto"
            />
          </div>
        )}
      </div>
    );
  };

  const getSearchPlaceholder = () => {
    switch (contentType) {
      case 'agent':
        return 'Search AI agents...';
      case 'mcp_server':
        return 'Search MCP servers...';
      case 'prompt':
      default:
        return 'Search prompts...';
    }
  };

  return (
    <>
      <OnboardingModal
        isOpen={showOnboarding}
        onClose={() => setShowOnboarding(false)}
      />

      <div className="max-w-7xl mx-auto px-4 py-8">
        <SEOHead
          title={`Browse ${getContentTypeName(contentType, true)} - Boop`}
          description={`Explore community-rated ${getContentTypeName(contentType, true).toLowerCase()} for AI development. Filter by category and rating.`}
          url={`${window.location.origin}/dashboard?type=${contentType}`}
          type="website"
        />

        {/* Header with Content Type Switcher */}
        <div className="mb-8">
          <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 mb-6">
            <h1 className="text-3xl font-bold">
              Discover {getContentTypeName(contentType, true)}
            </h1>
            <Link
              to={getCreateLink()}
              className="bg-boop-primary text-white px-6 py-2 rounded-lg hover:bg-boop-secondary transition flex items-center justify-center gap-2"
            >
              <span aria-hidden="true">{getContentTypeIcon(contentType)}</span>
              <span>Create {getContentTypeName(contentType)}</span>
            </Link>
          </div>

          {/* Content Type Switcher */}
          <div className="flex justify-center mb-6">
            <ContentTypeSwitcher
              activeType={contentType}
              onChange={setContentType}
              types={['prompt', 'agent', 'mcp_server']}
              variant="tabs"
              size="md"
            />
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-md mb-6 flex flex-wrap gap-4">
          <input
            type="text"
            placeholder={getSearchPlaceholder()}
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            className="flex-1 min-w-[200px] px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          />
          <select
            value={filters.category}
            onChange={(e) => setFilters({ ...filters, category: e.target.value })}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          >
            <option value="">All Categories</option>
            {categories.map((cat) => (
              <option key={cat.id} value={cat.id}>
                {cat.icon} {cat.name}
              </option>
            ))}
          </select>
          <select
            value={filters.sortBy}
            onChange={(e) => setFilters({ ...filters, sortBy: e.target.value })}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          >
            <option value="created_at">Newest</option>
            <option value="bayesian_rating">Top Rated</option>
            <option value="views_count">Most Viewed</option>
          </select>
        </div>

        {/* Content Grid */}
        {loading ? (
          <div className="flex flex-col items-center justify-center py-12 gap-4">
            <LoadingSpinner size="lg" text={`Loading ${getContentTypeName(contentType, true).toLowerCase()}...`} />
          </div>
        ) : content.length === 0 ? (
          <EmptyState
            title={`No ${getContentTypeName(contentType, true).toLowerCase()} found`}
            message={`Be the first to create a ${getContentTypeName(contentType).toLowerCase()}!`}
            action={
              <Link
                to={getCreateLink()}
                className="inline-flex items-center gap-2 px-4 py-2 bg-boop-primary text-white rounded-lg hover:bg-boop-secondary transition"
              >
                <span aria-hidden="true">{getContentTypeIcon(contentType)}</span>
                <span>Create {getContentTypeName(contentType)}</span>
              </Link>
            }
          />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {content.map((item) => renderContentCard(item))}
          </div>
        )}
      </div>
    </>
  );
};

export default Dashboard;
